




































































import { Component, Prop } from 'vue-property-decorator';
import { MapBlockViewModel, MapLocationBlockViewModel } from '@/types/contentServerContract';
import AspectRatioBlockContainer from '@/project/app/blocks/AspectRatioBlockContainer.vue';
import { Map as GmapMap, Marker as GmapMarker, InfoWindow as GmapInfoWindow } from 'gmap-vue';
import serverContextService from '@/core/serverContext.service';

interface Position {
    lat: number;
    lng: number;
}

// ref: https://developers.google.com/maps/documentation/javascript/reference/marker#MarkerOptions
interface Marker {
    clickable: boolean;
    draggable: boolean;
    icon: {
        url: string;
        size: {
            width: number;
            height: number;
        },
        scaledSize: {
            width: number;
            height: number;
        }
    };
    position: Position;
    data: MapLocationBlockViewModel;
}

// ref: https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
interface MapOptions {

    tilt?: number;
    zoom?: number; // The initial Map zoom level. Valid values: Integers between 0 - 18.
    minZoom?: number;
    maxZoom?: number;
    zoomControl?: boolean; // The enabled/disabled state of the Zoom control.
    zoomControlOptions?: {
        position: 'BOTTOM_CENTER' | 'BOTTOM_LEFT' | 'BOTTOM_RIGHT' | 'LEFT_BOTTOM' | 'LEFT_CENTER' | 'LEFT_TOP' | 'RIGHT_BOTTOM' | 'RIGHT_CENTER' | 'RIGHT_TOP' | 'TOP_CENTER' | 'TOP_LEFT' | 'TOP_RIGHT';
    },
    mapTypeControl: boolean;
    scaleControl: boolean;
    streetViewControl: boolean;
    rotateControl: boolean;
    fullscreenControl: boolean;
    disableDefaultUI: boolean;
    draggable: boolean;
    clickableIcons: boolean;
    scrollwheel?: boolean;
    disableDoubleClickZoom?: boolean;
    keyboardShortcuts?: boolean;
    styles: any[];
}

@Component({
    components: { GmapMap, GmapMarker, GmapInfoWindow }
})
export default class GoogleMapsBlock extends AspectRatioBlockContainer {
    @Prop({
        required: true,
        type: Object
    }) content!: MapBlockViewModel;

    @Prop({
        required: true,
        type: Boolean
    }) isFirstBlock!: boolean;

    private center: Position = { lat: 55.838395, lng: 12.402816 };
    private infoWindow: {
        activeMarker: Marker | null,
        position: Position | null,
        open: boolean
    } = {
        activeMarker: null,
        position: null,
        open: false
    }

    private defaultZoom: number = 6;

    private markerClicked(marker: Marker) {
        this.infoWindow.activeMarker = marker;
        this.infoWindow.position = marker.position;
        this.infoWindow.open = true;
    }

    private hideInfoWindow() {
        this.infoWindow.open = false;
        this.infoWindow.position = null;
        this.infoWindow.activeMarker = null;
    }

    private get markers(): Marker[] {
        const markerOffice = require(this.isOprema ? '@/assets/icons/map-marker-turquoise.svg' : '@/assets/icons/map-marker-blue.svg');
        const markerOther = require(this.isOprema ? '@/assets/icons/map-marker-black.svg' : '@/assets/icons/map-marker-green.svg');
        return (this.content.mapLocations)
            .map(({ content }) => content as MapLocationBlockViewModel)
            .map(x => ({
                clickable: true,
                draggable: false,
                icon: {
                    url: x.locationType === 'Office'
                        ? markerOffice
                        : markerOther,
                    size: {
                        width: 20,
                        height: 20
                    },
                    scaledSize: {
                        width: 20,
                        height: 20
                    }
                },
                position: {
                    lat: parseFloat(x.gpsLatitude),
                    lng: parseFloat(x.gpsLongitude)
                },
                data: x
            } as Marker));
    }

    private styles =
         [
             {
                 featureType: 'administrative',
                 elementType: 'all',
                 stylers: [
                     {
                         visibility: 'off'
                     }
                 ]
             },
             {
                 featureType: 'landscape',
                 elementType: 'all',
                 stylers: [
                     {
                         visibility: 'on'
                     },
                     {
                         color: this.isOprema ? '#c0dfe7' : '#c5d2e0'
                     }
                 ]
             },
             {
                 featureType: 'landscape',
                 elementType: 'geometry',
                 stylers: [
                     {
                         visibility: 'on'
                     }
                 ]
             },
             {
                 featureType: 'landscape',
                 elementType: 'labels',
                 stylers: [
                     {
                         visibility: 'off'
                     }
                 ]
             },
             {
                 featureType: 'poi',
                 elementType: 'all',
                 stylers: [
                     {
                         visibility: 'off'
                     }
                 ]
             },
             {
                 featureType: 'road',
                 elementType: 'all',
                 stylers: [
                     {
                         visibility: 'off'
                     }
                 ]
             },
             {
                 featureType: 'transit',
                 elementType: 'all',
                 stylers: [
                     {
                         visibility: 'off'
                     }
                 ]
             },
             {
                 featureType: 'water',
                 elementType: 'all',
                 stylers: [
                     {
                         color: '#ffffff'
                     }
                 ]
             }
         ];

    private get options(): MapOptions {
        return {
            maxZoom: this.content.maxZoom ? this.content.maxZoom : 9,
            minZoom: this.content.minZoom ? this.content.minZoom : 4,
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            disableDefaultUI: true,
            draggable: true,
            clickableIcons: true,
            scrollwheel: false,
            disableDoubleClickZoom: true,
            keyboardShortcuts: false,
            styles: this.styles
        };
    }

    get isOprema() {
        return serverContextService?.isOprema;
    }

    private async getMap() {
        return await this.$refs.map.$mapPromise;
    }

    private async setBoundsByMarkers() {
        const map = await this.getMap();

        var bounds = new window.google.maps.LatLngBounds();

        for (const marker of this.markers) {
            bounds.extend(marker.position);
        }

        map.fitBounds(bounds);
        map.panToBounds(bounds);

        // fitBounds will zoom in as much as possible, so we zoom back out if thats the case
        setTimeout(() => {
            if (map.getZoom() > this.defaultZoom) {
                map.setZoom(this.defaultZoom);
            }
        }, 500);
    }

    private async syncCenterLocationWithUser() {
        if (!navigator.geolocation) return;

        const map = await this.getMap();

        navigator.geolocation.getCurrentPosition(({ coords }) => {
            map.setCenter({
                lat: coords.latitude,
                lng: coords.longitude
            });
        });
    }

    async mounted() {
        await this.$gmapApiPromiseLazy();

        if (this.markers.length) {
            this.setBoundsByMarkers();
        } else {
            this.syncCenterLocationWithUser();
        }
    }

    public $refs!: {
        map: any;
    }
}
